import { useEffect, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";

/**
 * NotificationBell — header-mounted notification dropdown.
 * Re-uses /me/overview's recent_reviews payload (passed in as `items`).
 * Click → small dropdown panel. "Mark all read" hits the same /notifications/seen endpoint.
 */
export default function NotificationBell({ items = [], onDismiss }) {
  const [open, setOpen] = useState(false);
  const count = items?.length || 0;

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest?.("[data-testid='notif-bell-root']")) setOpen(false);
    };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/ambassador/me/notifications/seen");
    } catch (err) {
      console.warn("Failed to mark notifications seen", err);
    }
    setOpen(false);
    onDismiss?.();
  };

  return (
    <div data-testid="notif-bell-root" className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid="notif-bell"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-[#A855F7]/60 hover:text-white"
        aria-label={`Notifications${count ? ` (${count} new)` : ""}`}
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span data-testid="notif-dot" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#A855F7] px-1 text-[9px] font-bold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-[64px] sm:top-11 z-30 sm:w-80 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-zinc-900 px-4 py-2.5">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Notifications</p>
            {count > 0 && (
              <button onClick={markAll} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#A855F7] hover:underline" data-testid="notif-mark-all">
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-80 divide-y divide-zinc-900 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-4 py-6 text-center text-xs text-zinc-500">You&apos;re all caught up.</li>
            )}
            {items.slice(0, 10).map((it) => {
              if (it.kind === "streak") {
                return (
                  <li key={it.id} className="px-4 py-3 text-sm">
                    <p className="flex items-baseline gap-2">
                      <span>{it.emoji || "🔥"}</span>
                      <span className="flex-1 text-white">
                        <span className="font-bold">{it.weeks}-week streak!</span> <span className="text-zinc-400">Streak bonus unlocked</span>
                        {it.points_awarded > 0 && <span className="ml-1 text-[#A855F7]">· +{it.points_awarded} pts</span>}
                      </span>
                    </p>
                  </li>
                );
              }
              if (it.kind === "referral") {
                const pts = (it.points_awarded || 0) + (it.conversion_bonus_awarded || 0);
                return (
                  <li key={it.id} className="px-4 py-3 text-sm">
                    <p className="flex items-baseline gap-2">
                      <span>📈</span>
                      <span className="flex-1 text-white">
                        New signup on <span className="capitalize">{(it.product_key || "").replace(/-/g, " ")}</span>
                        {pts > 0 && <span className="ml-1 text-[#A855F7]">· +{pts} pts</span>}
                      </span>
                    </p>
                    {it.referred_email && <p className="ml-6 mt-0.5 text-[11px] text-zinc-500">{it.referred_email}</p>}
                  </li>
                );
              }
              return (
                <li key={it.id} className="px-4 py-3 text-sm">
                  <p className="flex items-baseline gap-2">
                    <span>{it.status === "approved" ? "✅" : "❌"}</span>
                    <span className="flex-1 text-white capitalize">
                      {(it.content_type || "").replace(/_/g, " ")}{" "}
                      <span className={it.status === "approved" ? "text-emerald-300" : "text-rose-300"}>
                        {it.status === "approved" ? `· +${it.points_awarded} pts` : "· rejected"}
                      </span>
                    </span>
                  </p>
                  {it.rejection_reason && <p className="ml-6 mt-0.5 text-[11px] text-zinc-500">Reason: {it.rejection_reason}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
